import { useCallback } from "react";

import { bulkUpdateWorkspaceNodePositions } from "../../../services/workspacesApi";
import type { WorkspaceDetail } from "../../../types/workspace";
import { toastWorkspaceApiError } from "../../../utils/workspaceApiErrors";
import { useWorkspaceContext } from "./workspaceContext";
import { layoutWorkspaceGraph } from "./layoutStrategyGraphSequential";
import { workspaceGraphPositionsToBulkPayload } from "./workspaceDagreLayout";
import { useBillingContext } from "../../../contexts/BillingContext";

export function useWorkspaceAutoLayout(): {
  applyAutoLayout: (detailOverride?: WorkspaceDetail | null) => Promise<boolean>;
} {
  const {
    detail,
    scope,
    workspaceId,
    viewMode,
    layoutMode,
    refreshDetail,
    patchNodePositions,
  } = useWorkspaceContext();
  const { simsPerReport } = useBillingContext();

  const applyAutoLayout = useCallback(
    async (detailOverride?: WorkspaceDetail | null): Promise<boolean> => {
      const graph = detailOverride ?? detail;
      if (!graph || !scope || !workspaceId || graph.nodes.length === 0) return false;

      try {
        const positions = layoutWorkspaceGraph(graph.nodes, graph.edges, {
          viewMode,
          // Always pack tight — large branch corridors are never useful on canvas.
          compact: true,
          layoutMode: viewMode === "strategy" ? layoutMode : "parallel",
          maxSimulationsPerReport: simsPerReport,
        });
        const localPayload = Array.from(positions.entries()).map(([nodeId, position]) => ({
          nodeId,
          x: position.x,
          y: position.y,
        }));
        const apiPayload = workspaceGraphPositionsToBulkPayload(positions, graph.nodes);
        patchNodePositions(localPayload);
        if (viewMode !== "strategy" && apiPayload.length > 0) {
          await bulkUpdateWorkspaceNodePositions(scope, workspaceId, {
            positions: apiPayload,
          });
        }
        if (viewMode !== "strategy") {
          await refreshDetail();
        }
        return true;
      } catch (err) {
        toastWorkspaceApiError(err, "Auto-layout failed");
        return false;
      }
    },
    [detail, layoutMode, patchNodePositions, refreshDetail, scope, simsPerReport, viewMode, workspaceId]
  );

  return { applyAutoLayout };
}
